"use client";

import SiteNav from "@/components/SiteNav";
import { useState, useRef, useEffect, useCallback } from "react";
import { Send, Car, Zap, Scale, DollarSign, RotateCcw, Loader2, Plus, MessageSquare, Trash2, PanelLeftClose, PanelLeft } from "lucide-react";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
}

interface Conversation {
  id: string;
  title: string;
  messages: Message[];
  updatedAt: number;
}

const STORAGE_KEY = "helveticcars-chat-history";

function loadConversations(): Conversation[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveConversations(convos: Conversation[]) {
  if (typeof window === "undefined") return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(convos));
}

function generateTitle(messages: Message[]): string {
  const firstUser = messages.find(m => m.role === "user");
  if (!firstUser) return "Nouvelle conversation";
  const text = firstUser.content.slice(0, 50);
  return text.length < firstUser.content.length ? text + "..." : text;
}

const SUGGESTIONS = [
  { icon: Scale, text: "Compare Tesla Model 3 vs Xpeng G6" },
  { icon: Car, text: "Quelle voiture familiale importer pour 30'000 CHF ?" },
  { icon: Zap, text: "Meilleur SUV electrique rapport qualite/prix ?" },
  { icon: DollarSign, text: "Combien j'economise en important une BMW Serie 3 ?" },
];

function MarkdownLight({ content }: { content: string }) {
  const lines = content.split("\n");
  const elements: React.ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith("### ")) {
      elements.push(<h4 key={key++} className="font-bold text-graphite mt-4 mb-1">{line.slice(4)}</h4>);
    } else if (line.startsWith("## ")) {
      elements.push(<h3 key={key++} className="font-bold text-graphite mt-4 mb-2 text-lg">{line.slice(3)}</h3>);
    } else if (line.startsWith("| ")) {
      const rows: string[] = [];
      while (i < lines.length && lines[i].startsWith("|")) {
        rows.push(lines[i]);
        i++;
      }
      const headerCells = rows[0]?.split("|").filter(Boolean).map(c => c.trim()) || [];
      const dataRows = rows.slice(2);
      elements.push(
        <div key={key++} className="overflow-x-auto my-3">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr>
                {headerCells.map((cell, ci) => (
                  <th key={ci} className="text-left px-3 py-2 bg-warm-gray font-semibold text-graphite border border-border">{cell}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dataRows.map((row, ri) => {
                const cells = row.split("|").filter(Boolean).map(c => c.trim());
                return (
                  <tr key={ri}>
                    {cells.map((cell, ci) => (
                      <td key={ci} className="px-3 py-1.5 border border-border">{inlineFormat(cell)}</td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
      continue;
    } else if (line.startsWith("- ") || line.startsWith("* ")) {
      const items: string[] = [];
      while (i < lines.length && (lines[i].startsWith("- ") || lines[i].startsWith("* "))) {
        items.push(lines[i].slice(2));
        i++;
      }
      elements.push(
        <ul key={key++} className="list-disc pl-5 my-2 space-y-1">
          {items.map((item, idx) => <li key={idx}>{inlineFormat(item)}</li>)}
        </ul>
      );
      continue;
    } else if (/^\d+\. /.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\. /.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\. /, ""));
        i++;
      }
      elements.push(
        <ol key={key++} className="list-decimal pl-5 my-2 space-y-1">
          {items.map((item, idx) => <li key={idx}>{inlineFormat(item)}</li>)}
        </ol>
      );
      continue;
    } else if (line.trim() === "") {
      elements.push(<div key={key++} className="h-2" />);
    } else {
      elements.push(<p key={key++} className="my-1">{inlineFormat(line)}</p>);
    }
    i++;
  }
  return <>{elements}</>;
}

function inlineFormat(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let k = 0;
  while (remaining.length > 0) {
    const bold = remaining.match(/\*\*(.+?)\*\*/);
    if (bold && bold.index !== undefined) {
      if (bold.index > 0) parts.push(remaining.slice(0, bold.index));
      parts.push(<strong key={k++} className="font-semibold">{bold[1]}</strong>);
      remaining = remaining.slice(bold.index + bold[0].length);
    } else {
      parts.push(remaining);
      break;
    }
  }
  return parts.length === 1 && typeof parts[0] === "string" ? parts[0] : <>{parts}</>;
}

export default function ConseillerPage() {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Load history on mount
  useEffect(() => {
    const loaded = loadConversations();
    setConversations(loaded);
  }, []);

  // Save messages to conversation when they change
  useEffect(() => {
    if (!activeId || messages.length === 0) return;
    // Don't save if last assistant message is still empty (streaming)
    const lastMsg = messages[messages.length - 1];
    if (lastMsg.role === "assistant" && !lastMsg.content) return;

    setConversations(prev => {
      const updated = prev.map(c =>
        c.id === activeId
          ? { ...c, messages, title: generateTitle(messages), updatedAt: Date.now() }
          : c
      );
      saveConversations(updated);
      return updated;
    });
  }, [messages, activeId]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  function startNewConversation() {
    const newConvo: Conversation = {
      id: Date.now().toString(),
      title: "Nouvelle conversation",
      messages: [],
      updatedAt: Date.now(),
    };
    setConversations(prev => {
      const updated = [newConvo, ...prev];
      saveConversations(updated);
      return updated;
    });
    setActiveId(newConvo.id);
    setMessages([]);
    setInput("");
    setSidebarOpen(false);
    inputRef.current?.focus();
  }

  function loadConversation(id: string) {
    const convo = conversations.find(c => c.id === id);
    if (convo) {
      setActiveId(convo.id);
      setMessages(convo.messages);
      setSidebarOpen(false);
    }
  }

  function deleteConversation(id: string) {
    setConversations(prev => {
      const updated = prev.filter(c => c.id !== id);
      saveConversations(updated);
      return updated;
    });
    if (activeId === id) {
      setActiveId(null);
      setMessages([]);
    }
  }

  async function sendMessage(text?: string) {
    const content = text || input.trim();
    if (!content || isStreaming) return;

    // Create conversation if none active
    let currentId = activeId;
    if (!currentId) {
      const newConvo: Conversation = {
        id: Date.now().toString(),
        title: content.slice(0, 50),
        messages: [],
        updatedAt: Date.now(),
      };
      setConversations(prev => {
        const updated = [newConvo, ...prev];
        saveConversations(updated);
        return updated;
      });
      currentId = newConvo.id;
      setActiveId(currentId);
    }

    const userMsg: Message = { id: Date.now().toString(), role: "user", content };
    const assistantMsg: Message = { id: (Date.now() + 1).toString(), role: "assistant", content: "" };

    setMessages(prev => [...prev, userMsg, assistantMsg]);
    setInput("");
    setIsStreaming(true);

    try {
      const allMessages = [...messages, userMsg].map(m => ({
        role: m.role,
        content: m.content,
      }));

      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: allMessages }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Erreur" }));
        setMessages(prev =>
          prev.map(m => m.id === assistantMsg.id ? { ...m, content: err.error || "Une erreur est survenue." } : m)
        );
        setIsStreaming(false);
        return;
      }

      const reader = res.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (reader) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6);
            if (data === "[DONE]") break;
            try {
              const parsed = JSON.parse(data);
              if (parsed.text) {
                setMessages(prev =>
                  prev.map(m => m.id === assistantMsg.id ? { ...m, content: m.content + parsed.text } : m)
                );
              }
            } catch {}
          }
        }
      }
    } catch {
      setMessages(prev =>
        prev.map(m => m.id === assistantMsg.id ? { ...m, content: "Erreur de connexion. Reessayez." } : m)
      );
    }

    setIsStreaming(false);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  const isEmpty = messages.length === 0;

  return (
    <>
      <SiteNav />
      <div className="flex min-h-screen bg-offwhite pt-16">
        {/* Sidebar - conversation history */}
        <div className={`fixed inset-y-0 left-0 z-40 pt-16 w-72 bg-white border-r border-border transform transition-transform duration-200 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} md:relative md:translate-x-0 md:pt-0 ${sidebarOpen ? "" : "md:hidden"}`}>
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between p-3 border-b border-border">
              <span className="text-sm font-semibold text-graphite">Historique</span>
              <div className="flex gap-1">
                <button onClick={startNewConversation} className="p-1.5 hover:bg-warm-gray rounded transition-colors" title="Nouvelle conversation">
                  <Plus size={16} className="text-graphite/60" />
                </button>
                <button onClick={() => setSidebarOpen(false)} className="p-1.5 hover:bg-warm-gray rounded transition-colors md:hidden">
                  <PanelLeftClose size={16} className="text-graphite/60" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {conversations.length === 0 ? (
                <p className="text-xs text-graphite/30 text-center py-8">Aucune conversation</p>
              ) : (
                conversations
                  .sort((a, b) => b.updatedAt - a.updatedAt)
                  .map(convo => (
                    <div
                      key={convo.id}
                      className={`group flex items-center gap-2 px-3 py-2.5 cursor-pointer hover:bg-warm-gray/50 transition-colors ${activeId === convo.id ? "bg-warm-gray" : ""}`}
                      onClick={() => loadConversation(convo.id)}
                    >
                      <MessageSquare size={14} className="text-graphite/30 shrink-0" />
                      <span className="flex-1 text-sm text-graphite/70 truncate">{convo.title}</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteConversation(convo.id); }}
                        className="opacity-0 group-hover:opacity-100 p-1 hover:text-swiss-red transition-all"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))
              )}
            </div>
          </div>
        </div>

        {/* Overlay for mobile sidebar */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-30 bg-black/20 md:hidden" onClick={() => setSidebarOpen(false)} />
        )}

        {/* Main chat area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Top bar with sidebar toggle */}
          <div className="flex items-center gap-2 px-4 py-2 border-b border-border bg-offwhite">
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-1.5 hover:bg-warm-gray rounded transition-colors">
              <PanelLeft size={18} className="text-graphite/50" />
            </button>
            <button onClick={startNewConversation} className="p-1.5 hover:bg-warm-gray rounded transition-colors" title="Nouvelle conversation">
              <Plus size={18} className="text-graphite/50" />
            </button>
            {activeId && (
              <span className="text-xs text-graphite/30 truncate">
                {conversations.find(c => c.id === activeId)?.title}
              </span>
            )}
          </div>

          {/* Chat area */}
          <div className="flex-1 overflow-y-auto">
            {isEmpty ? (
              <div className="flex flex-col items-center justify-center min-h-[calc(100vh-220px)] px-6">
                <div className="w-14 h-14 bg-swiss-red flex items-center justify-center mb-6">
                  <Car size={28} className="text-white" />
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-graphite font-heading text-center">
                  Quel vehicule vous correspond ?
                </h1>
                <p className="mt-3 text-graphite/60 text-center max-w-md">
                  Comparez des modeles, estimez les couts d'import et trouvez la voiture ideale avec l'aide de notre conseiller IA.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-8 w-full max-w-2xl">
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => sendMessage(s.text)}
                      className="flex items-start gap-3 p-4 text-left bg-white border border-border hover:border-swiss-red/30 hover:shadow-md transition-all group"
                    >
                      <s.icon size={18} className="text-graphite/40 group-hover:text-swiss-red mt-0.5 shrink-0" />
                      <span className="text-sm text-graphite/80 group-hover:text-graphite">{s.text}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
                {messages.map((msg) => (
                  <div key={msg.id} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] ${msg.role === "user"
                      ? "bg-graphite text-white px-4 py-3"
                      : "bg-white border border-border px-5 py-4"
                    }`}>
                      {msg.role === "assistant" ? (
                        msg.content ? (
                          <div className="text-sm text-graphite/90 leading-relaxed">
                            <MarkdownLight content={msg.content} />
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-sm text-graphite/40">
                            <Loader2 size={14} className="animate-spin" /> Reflexion...
                          </div>
                        )
                      ) : (
                        <p className="text-sm">{msg.content}</p>
                      )}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Input bar */}
          <div className="sticky bottom-0 bg-offwhite border-t border-border">
            <div className="max-w-3xl mx-auto px-4 py-4">
              <div className="flex items-end gap-2 bg-white border border-border focus-within:border-swiss-red/50 transition-colors">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Decrivez votre besoin ou comparez des modeles..."
                  rows={1}
                  className="flex-1 resize-none bg-transparent px-4 py-3 text-sm focus:outline-none text-graphite placeholder:text-graphite/30"
                  style={{ minHeight: "44px", maxHeight: "120px" }}
                  onInput={(e) => {
                    const target = e.target as HTMLTextAreaElement;
                    target.style.height = "auto";
                    target.style.height = Math.min(target.scrollHeight, 120) + "px";
                  }}
                />
                <button
                  onClick={() => sendMessage()}
                  disabled={!input.trim() || isStreaming}
                  className="p-3 text-graphite/40 hover:text-swiss-red disabled:opacity-30 transition-colors"
                >
                  <Send size={18} />
                </button>
              </div>
              <p className="text-[10px] text-graphite/30 text-center mt-2">
                Conseiller IA — les informations sont indicatives, contactez-nous pour un devis precis
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
